/**
 * Play log tracker — records asset play start/end timestamps and batches them
 * for submission to the CMS every 5 minutes.
 *
 * Usage:
 *   import { usePlayLog } from './playlog';
 *
 *   // In your playback component:
 *   const { onAssetStart, onAssetEnd, flush } = usePlayLog({
 *     deviceId: credentials.deviceId,
 *     screenId: credentials.screenId,
 *     jwt: credentials.jwt,
 *   });
 *
 *   // Pass callbacks to usePlayback:
 *   usePlayback({ onAssetStart, onAssetEnd, ... });
 *
 * The tracker:
 * 1. Records start timestamp when an asset begins playing
 * 2. Records end timestamp when an asset stops or switches
 * 3. Computes duration from startedAt → endedAt
 * 4. Batches completed logs and POSTs to CMS every 5 minutes
 * 5. Flushes remaining logs on page unload (via keepalive fetch)
 */

import { useState, useEffect, useRef, useCallback } from 'react';
import { CMS_API_BASE } from '../types';

/** Batch interval in milliseconds (5 minutes) */
const BATCH_INTERVAL_MS = 5 * 60 * 1000;

/** Maximum logs to keep in memory (safety cap) */
const MAX_BUFFER_SIZE = 1000;

/** Single play log entry (completed) */
export interface CompletedPlayLog {
  assetId: string;
  campaignId: string | null;
  startedAt: string;
  endedAt: string;
  duration: number;
  /** Set when a recycle interaction closed this entry early (impression firewall). */
  interrupted?: 'INTERACTION';
}

/** In-progress play log (start recorded, waiting for end) */
interface ActivePlayLog {
  assetId: string;
  startedAt: Date;
  campaignId: string | null;
}

/** Options for usePlayLog */
export interface UsePlayLogOptions {
  deviceId: string;
  screenId: string;
  /** JWT bearer token for authenticating with the CMS playlog API */
  jwt: string;
  /** Optional: flush interval override in ms (default: 300000 = 5 min) */
  flushIntervalMs?: number;
}

/** Hook return value */
export interface UsePlayLogReturn {
  /** Callback to pass to usePlayback.onAssetStart */
  onAssetStart: (assetId: string, campaignId?: string | null) => void;
  /** Callback to pass to usePlayback.onAssetEnd */
  onAssetEnd: (assetId: string) => void;
  /** Manually flush logs to CMS */
  flush: () => Promise<void>;
  /** Number of pending logs in buffer */
  pendingCount: number;
  /** Close the in-flight entry immediately, tagged interrupted, with honest duration. */
  closeActiveForInteraction: () => void;
  /** When true, onAssetStart/onAssetEnd are no-ops (overlay non-idle → no billable plays). */
  setInteractionActive: (active: boolean) => void;
}

function log(...args: unknown[]) {
  // Dev-only: silent in production kiosk builds to avoid unbounded console
  // growth on 24/7 devices.
  if (import.meta.env.DEV) console.log('[PlayLog]', ...args);
}

/**
 * POST a batch of play logs to the CMS API.
 */
async function postPlayLogBatch(
  deviceId: string,
  screenId: string,
  jwt: string,
  logs: CompletedPlayLog[],
): Promise<boolean> {
  if (logs.length === 0) return true;

  try {
    const response = await fetch(`${CMS_API_BASE}/api/playlogs`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${jwt}`,
      },
      body: JSON.stringify({ deviceId, screenId, logs }),
    });

    if (response.ok) {
      const result = await response.json();
      log(`Flushed ${result.ingested ?? logs.length} play logs to CMS`);
      return true;
    }

    const errorText = await response.text().catch(() => 'Unknown error');
    log(`Failed to flush play logs: ${response.status} ${errorText}`);
    return false;
  } catch (err) {
    log(`Network error flushing play logs: ${err instanceof Error ? err.message : err}`);
    return false;
  }
}

/**
 * Send a batch of play logs using a keepalive fetch (for page unload).
 *
 * navigator.sendBeacon cannot set custom headers, so it can't carry the
 * Authorization token the CMS requires. A keepalive fetch can set headers
 * and survives page unload in Chromium (the player runtime), making it the
 * correct primitive for this fire-and-forget send.
 */
async function sendKeepaliveBatch(
  deviceId: string,
  screenId: string,
  jwt: string,
  logs: CompletedPlayLog[],
): Promise<boolean> {
  if (logs.length === 0) return true;

  try {
    const response = await fetch(`${CMS_API_BASE}/api/playlogs`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${jwt}`,
      },
      body: JSON.stringify({ deviceId, screenId, logs }),
      keepalive: true,
    });
    return response.ok;
  } catch (err) {
    log(`Network error sending keepalive play logs: ${err instanceof Error ? err.message : err}`);
    return false;
  }
}

/**
 * React hook for tracking proof-of-play logs.
 *
 * Maintains an in-memory buffer of completed play logs and periodically
 * flushes them to the CMS API. Also flushes on page visibility change
 * and unload events.
 */
export function usePlayLog(options: UsePlayLogOptions): UsePlayLogReturn {
  const { deviceId, screenId, jwt, flushIntervalMs = BATCH_INTERVAL_MS } = options;

  // Buffer of completed play logs waiting to be flushed
  const completedLogsRef = useRef<CompletedPlayLog[]>([]);

  // Currently active (playing) log — waiting for end timestamp
  const activeLogRef = useRef<ActivePlayLog | null>(null);

  // Flush timer
  const flushTimerRef = useRef<ReturnType<typeof setInterval> | null>(null);

  // Pending count state (for UI if needed)
  const [pendingCount, setPendingCount] = useState(0);

  // Impression firewall: while true, onAssetStart/onAssetEnd are no-ops.
  // A ref (not state) is used so the guard is synchronous and doesn't
  // require callback dependency churn.
  const interactionActiveRef = useRef(false);

  // ─── Flush function ──────────────────────────────────────────────────

  const flush = useCallback(async () => {
    const logs = completedLogsRef.current;
    if (logs.length === 0) {
      log('No play logs to flush');
      return;
    }

    // Take logs from buffer
    completedLogsRef.current = [];
    setPendingCount(0);

    const success = await postPlayLogBatch(deviceId, screenId, jwt, logs);

    if (!success) {
      // Put logs back at the beginning of the buffer for retry
      completedLogsRef.current = [...logs, ...completedLogsRef.current];
      setPendingCount(completedLogsRef.current.length);
      log(`Retaining ${completedLogsRef.current.length} logs for next flush`);
    }
  }, [deviceId, screenId, jwt]);

  // ─── Asset start callback ────────────────────────────────────────────

  const onAssetStart = useCallback((assetId: string, campaignId?: string | null) => {
    if (interactionActiveRef.current) return; // firewall: no billable plays during interaction

    // If there's an active log, end it first (asset was interrupted)
    if (activeLogRef.current) {
      const active = activeLogRef.current;
      const endedAt = new Date();
      const duration = Math.round((endedAt.getTime() - active.startedAt.getTime()) / 1000);

      const completedLog: CompletedPlayLog = {
        assetId: active.assetId,
        campaignId: active.campaignId,
        startedAt: active.startedAt.toISOString(),
        endedAt: endedAt.toISOString(),
        duration: Math.max(0, duration),
      };

      completedLogsRef.current.push(completedLog);
      if (completedLogsRef.current.length > MAX_BUFFER_SIZE) {
        completedLogsRef.current = completedLogsRef.current.slice(-MAX_BUFFER_SIZE);
      }
      setPendingCount(completedLogsRef.current.length);

      log(`Asset ${active.assetId} ended (interrupted by ${assetId}), duration: ${duration}s`);
    }

    // Start new active log
    activeLogRef.current = {
      assetId,
      startedAt: new Date(),
      campaignId: campaignId ?? null,
    };

    log(`Asset ${assetId} started playing`);
  }, []);

  // ─── Asset end callback ──────────────────────────────────────────────

  const onAssetEnd = useCallback((assetId: string) => {
    if (interactionActiveRef.current) return; // firewall

    const active = activeLogRef.current;

    if (!active || active.assetId !== assetId) {
      log(`Ignoring end event for ${assetId} (no matching start)`);
      return;
    }

    const endedAt = new Date();
    const duration = Math.round((endedAt.getTime() - active.startedAt.getTime()) / 1000);

    const completedLog: CompletedPlayLog = {
      assetId,
      campaignId: active.campaignId,
      startedAt: active.startedAt.toISOString(),
      endedAt: endedAt.toISOString(),
      duration: Math.max(0, duration),
    };

    completedLogsRef.current.push(completedLog);
    if (completedLogsRef.current.length > MAX_BUFFER_SIZE) {
      completedLogsRef.current = completedLogsRef.current.slice(-MAX_BUFFER_SIZE);
    }
    setPendingCount(completedLogsRef.current.length);

    activeLogRef.current = null;

    log(`Asset ${assetId} ended, duration: ${duration}s`);
  }, []);

  // ─── Flush on interval ──────────────────────────────────────────────

  useEffect(() => {
    flushTimerRef.current = setInterval(() => {
      flush();
    }, flushIntervalMs);

    return () => {
      if (flushTimerRef.current) {
        clearInterval(flushTimerRef.current);
        flushTimerRef.current = null;
      }
    };
  }, [flush, flushIntervalMs]);

  // ─── Flush on visibility change / unload / unmount (#43) ─────────────
  //
  // These handlers fire together on page hide (visibilitychange→hidden +
  // beforeunload + React unmount). Without dedup, a single batch can be
  // sent 2-3×. Fix: after any successful beacon send, clear the buffer
  // atomically so subsequent handlers see an empty buffer and skip.

  /**
   * Atomically flush the buffer: swap-and-send. Returns true if sent.
   * Uses a swap pattern so concurrent handlers can't double-send.
   */
  const flushBeacon = useCallback((): boolean => {
    // End any active log first
    if (activeLogRef.current) {
      const active = activeLogRef.current;
      const endedAt = new Date();
      const duration = Math.round((endedAt.getTime() - active.startedAt.getTime()) / 1000);
      completedLogsRef.current.push({
        assetId: active.assetId,
        campaignId: active.campaignId,
        startedAt: active.startedAt.toISOString(),
        endedAt: endedAt.toISOString(),
        duration: Math.max(0, duration),
      });
      activeLogRef.current = null;
    }

    const logs = completedLogsRef.current;
    if (logs.length === 0) return false;

    // Swap: clear the ref BEFORE sending, so a concurrent handler can't
    // re-send the same logs (#43).
    const logsToSend = logs.splice(0, logs.length);
    setPendingCount(0);

    // Fire the keepalive send. Because sendKeepaliveBatch is async and the
    // buffer was already swapped above, a concurrent unload handler will see
    // an empty buffer and skip — preserving the #43 dedup guarantee.
    void sendKeepaliveBatch(deviceId, screenId, jwt, logsToSend).then((success) => {
      if (success) {
        log(`Flushed ${logsToSend.length} play logs via keepalive fetch`);
      } else {
        // Send failed — push logs back for a future flush attempt
        completedLogsRef.current.unshift(...logsToSend);
        setPendingCount(completedLogsRef.current.length);
      }
    });
    return true;
  }, [deviceId, screenId, jwt]);

  useEffect(() => {
    function handleVisibilityChange() {
      if (document.visibilityState === 'hidden') {
        flushBeacon();
      }
    }

    document.addEventListener('visibilitychange', handleVisibilityChange);
    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, [flushBeacon]);

  useEffect(() => {
    function handleBeforeUnload() {
      flushBeacon();
    }

    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload);
    };
  }, [flushBeacon]);

  // ─── Cleanup ─────────────────────────────────────────────────────────

  useEffect(() => {
    return () => {
      // Final flush on unmount
      flushBeacon();
    };
  }, [flushBeacon]);

  // ─── Impression firewall (recycle interactions) ──────────────────────

  const closeActiveForInteraction = useCallback(() => {
    const active = activeLogRef.current;
    if (!active) return;
    const endedAt = new Date();
    const duration = Math.round((endedAt.getTime() - active.startedAt.getTime()) / 1000);
    completedLogsRef.current.push({
      assetId: active.assetId,
      campaignId: active.campaignId,
      startedAt: active.startedAt.toISOString(),
      endedAt: endedAt.toISOString(),
      duration: Math.max(0, duration),
      interrupted: 'INTERACTION',
    });
    if (completedLogsRef.current.length > MAX_BUFFER_SIZE) {
      completedLogsRef.current = completedLogsRef.current.slice(-MAX_BUFFER_SIZE);
    }
    setPendingCount(completedLogsRef.current.length);
    activeLogRef.current = null;
    log(`Asset ${active.assetId} closed for INTERACTION, duration: ${duration}s`);
  }, []);

  const setInteractionActive = useCallback((active: boolean) => {
    interactionActiveRef.current = active;
  }, []);

  return {
    onAssetStart,
    onAssetEnd,
    flush,
    pendingCount,
    closeActiveForInteraction,
    setInteractionActive,
  };
}
